import React, { useEffect, useState, useCallback } from 'react';
import { FiBell, FiCheckCircle, FiAlertCircle, FiInfo, FiRefreshCw } from 'react-icons/fi';
import { portalAPI } from '../../services/api';

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getNotifIcon = (type) => {
  const t = (type || '').toLowerCase();
  if (t === 'info') return <FiInfo size={20} color="var(--primary-color)" />;
  if (t === 'warning' || t === 'overdue') return <FiAlertCircle size={20} color="var(--warning-color, #f59e0b)" />;
  if (t === 'success' || t === 'paid') return <FiCheckCircle size={20} color="var(--success-color, #10b981)" />;
  return <FiBell size={20} color="var(--text-secondary)" />;
};

const PortalNotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [markingAll, setMarkingAll] = useState(false);
  const [error, setError] = useState(null);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await portalAPI.notifications.list({ limit: 50 });
      const data = res.data;
      const items = Array.isArray(data?.notifications)
        ? data.notifications
        : Array.isArray(data)
        ? data
        : [];
      
      // Filtrer par sécurité côté client pour exclure les demandes de contact/d'intérêt
      const filtered = items.filter(n => {
        const title = (n.title || '').toLowerCase();
        return !title.includes('contact') && !title.includes('demande');
      });

      setNotifications(filtered);
      setUnreadCount(data?.unread_count ?? filtered.filter((n) => !n.is_read).length);
    } catch (err) {
      setError('Impossible de charger les notifications.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleMarkAsRead = async (notif) => {
    if (notif.is_read) return;
    try {
      await portalAPI.notifications.markAsRead(notif.id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === notif.id ? { ...n, is_read: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch {
      // silently fail
    }
  };

  const handleMarkAllAsRead = async () => {
    setMarkingAll(true);
    try {
      await portalAPI.notifications.markAllAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch {
      // silently fail
    } finally {
      setMarkingAll(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FiBell />
            Notifications
            {unreadCount > 0 && (
              <span className="badge badge-info" style={{ fontSize: 13 }}>
                {unreadCount} non lue{unreadCount > 1 ? 's' : ''}
              </span>
            )}
          </h1>
          <p style={{ color: 'var(--text-secondary)', marginTop: 4 }}>
            Vos alertes et messages importants.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {unreadCount > 0 && (
            <button
              className="btn btn-secondary"
              onClick={handleMarkAllAsRead}
              disabled={markingAll}
            >
              <FiCheckCircle />
              {markingAll ? 'En cours…' : 'Tout marquer comme lu'}
            </button>
          )}
          <button className="btn btn-secondary" onClick={fetchNotifications} disabled={loading}>
            <FiRefreshCw />
            Actualiser
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading">
          <div className="spinner" />
        </div>
      ) : error ? (
        <div className="card">
          <div className="card-body">
            <div className="empty-state">
              <FiAlertCircle size={40} color="var(--danger-color, #ef4444)" />
              <h3>{error}</h3>
              <button className="btn btn-primary" onClick={fetchNotifications}>
                Réessayer
              </button>
            </div>
          </div>
        </div>
      ) : notifications.length === 0 ? (
        <div className="card">
          <div className="card-body">
            <div className="empty-state">
              <FiBell size={48} />
              <h3>Aucune notification</h3>
              <p style={{ color: 'var(--text-secondary)' }}>
                Vous n'avez pas encore reçu de notification.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {notifications.map((notif) => (
            <div
              key={notif.id}
              className="card"
              onClick={() => handleMarkAsRead(notif)}
              style={{
                cursor: notif.is_read ? 'default' : 'pointer',
                borderLeft: notif.is_read ? undefined : '3px solid var(--primary-color)',
                background: notif.is_read ? undefined : 'var(--bg-hover)',
                transition: 'background 0.2s',
              }}
            >
              <div className="card-body" style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                <div style={{ flexShrink: 0, paddingTop: 2 }}>
                  {getNotifIcon(notif.type)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                    <div style={{ fontWeight: notif.is_read ? 400 : 600, fontSize: 15 }}>
                      {notif.title || 'Notification'}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {!notif.is_read && (
                        <span className="badge badge-info">Nouveau</span>
                      )}
                      <span style={{ color: 'var(--text-secondary)', fontSize: 12, whiteSpace: 'nowrap' }}>
                        {formatDate(notif.created_at || notif.date)}
                      </span>
                    </div>
                  </div>
                  {(notif.message || notif.body) && (
                    <div style={{ color: 'var(--text-secondary)', marginTop: 4, fontSize: 14 }}>
                      {notif.message || notif.body}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PortalNotificationsPage;
